import { useState, useEffect } from 'react';
import api from '../services/api';
import solanaDistributionWS from '../services/solanaDistributionWebSocket';
import { useTheme } from '../contexts/ThemeContext';
import { useToken } from '../contexts/TokenContext';

const SolanaDistributionPage = () => {
  const { isDarkMode } = useTheme();
  const { tokenId } = useToken();
  const [activeTab, setActiveTab] = useState('distribute');
  const [walletGroups, setWalletGroups] = useState([]);
  const [selectedSourceWallet, setSelectedSourceWallet] = useState(null);
  const [selectedDestWallet, setSelectedDestWallet] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [gathers, setGathers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [distributionResult, setDistributionResult] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [distributeMode, setDistributeMode] = useState('equal'); // equal, custom, random
  const [gatherMode, setGatherMode] = useState('all'); // all, custom
  const [totalAmount, setTotalAmount] = useState('');
  const [randomVariance, setRandomVariance] = useState(20); // Default 20% variance
  const [minimumRentExempt, setMinimumRentExempt] = useState(0.00203928); // Default fallback
  const [processSteps, setProcessSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);

  // Auto Distribution State
  const [autoDistributionSettings, setAutoDistributionSettings] = useState({
    enabled: false,
    funding_wallet_id: null,
    funding_wallet: null,
    top_up_amount: 0.01,
    minimum_balance: 0.009,
    include_grouped_wallets_only: true
  });
  const [availableFundingWallets, setAvailableFundingWallets] = useState([]);
  const [eligibleWallets, setEligibleWallets] = useState([]);
  const [autoDistributionLogs, setAutoDistributionLogs] = useState([]);
  const [autoDistributionLoading, setAutoDistributionLoading] = useState(false);

  // Helper function to format wallet addresses
  const formatAddress = (address, isSelected = false) => {
    if (!address) return '';
    if (isSelected) return address; // Show full address when selected
    return `${address.slice(0, 4)}...${address.slice(-4)}`; // Show first 4 + last 4
  };

  useEffect(() => {
    if (tokenId) {
      loadWalletsWithGroups();
      loadMinimumRentExempt();
    }
  }, [tokenId]);

  // Load auto distribution data when auto tab is selected AND wallet data is available
  useEffect(() => {
    if (activeTab === 'auto' && walletGroups.length > 0) {
      console.log('Auto tab selected and wallet data available, loading auto distribution data...');
      setAutoDistributionLoading(true);
      
      Promise.all([
        loadAutoDistributionSettings(),
        loadAvailableFundingWallets(),
        loadEligibleWallets(),
        loadAutoDistributionLogs()
      ]).finally(() => {
        setAutoDistributionLoading(false);
      });
    }
  }, [activeTab, walletGroups]);

  // Helper function to translate technical errors into user-friendly messages
  const translateError = (error) => {
    const message = error.toLowerCase();
    
    if (message.includes('insufficient funds for rent')) {
      return 'Insufficient balance - need more SOL or select fewer wallets';
    }
    if (message.includes('may have succeeded despite timeout')) {
      return 'Transaction likely succeeded - check blockchain explorer';
    }
    if (message.includes('block height exceeded') || message.includes('has expired')) {
      return 'Transaction timeout - may have succeeded anyway';
    }
    if (message.includes('simulation failed')) {
      return 'Transaction would fail - check wallet balances';
    }
    if (message.includes('insufficient balance')) {
      return 'Not enough SOL available in source wallet';
    }
    if (message.includes('account not found')) {
      return 'Wallet address not found on network';
    }
    if (message.includes('transaction too large')) {
      return 'Too many wallets selected - try smaller batches';
    }
    if (message.includes('network error') || message.includes('timeout')) {
      return 'Network connection issue - please retry';
    }
    
    // For any unrecognized error, return a generic message
    return 'Transfer failed - please check wallet status';
  };

  const loadMinimumRentExempt = async () => {
    try {
      const response = await api.get('/solana-distribution/minimum-rent-exempt');
      if (response.data.success) {
        setMinimumRentExempt(response.data.data.sol);
        console.log('Loaded minimum rent exempt:', response.data.data.sol, 'SOL');
      }
    } catch (error) {
      console.error('Failed to load minimum rent exempt:', error);
      // Keep using the fallback value
    }
  };

  const loadWalletsWithGroups = async () => {
    try {
      const response = await api.get('/solana-distribution/wallets-grouped', {
        params: { token_id: tokenId }
      });
      if (response.data.success) {
        console.log('Wallet groups received:', response.data.data.groups);
        setWalletGroups(response.data.data.groups);
        // Expand small groups by default, collapse large ones
        const expanded = {};
        response.data.data.groups.forEach(group => {
          // Only expand groups with 12 or fewer wallets by default
          expanded[group.id] = group.wallets.length <= 12;
        });
        setExpandedGroups(expanded);
      }
    } catch (error) {
      console.error('Failed to load wallets:', error);
      setError('Failed to load wallets');
    }
  };

  // Auto Distribution Functions
  const loadAutoDistributionSettings = async (retryCount = 0) => {
    try {
      const response = await api.get('/sol-auto-distribution/settings');
      if (response.data.success) {
        setAutoDistributionSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load auto distribution settings:', error);
      
      // If it's a 404 error and we haven't retried much, wait and retry
      if (error.response?.status === 404 && retryCount < 3) {
        console.log(`Settings API route not ready, retrying in ${(retryCount + 1) * 1000}ms... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => {
          loadAutoDistributionSettings(retryCount + 1);
        }, (retryCount + 1) * 1000);
      }
    }
  };

  const loadAvailableFundingWallets = async (retryCount = 0) => {
    try {
      const response = await api.get('/sol-auto-distribution/available-funding-wallets', {
        params: { token_id: tokenId }
      });
      console.log('Funding wallets response:', response.data);
      if (response.data.success) {
        setAvailableFundingWallets(response.data.data.wallets);
        console.log('Set funding wallets:', response.data.data.wallets);
      } else {
        console.error('API returned success: false', response.data);
        throw new Error('API returned success: false');
      }
    } catch (error) {
      console.error('Failed to load funding wallets:', error);
      
      // If it's a 404 error and we haven't retried much, wait and retry
      if (error.response?.status === 404 && retryCount < 3) {
        console.log(`API route not ready, retrying in ${(retryCount + 1) * 1000}ms... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => {
          loadAvailableFundingWallets(retryCount + 1);
        }, (retryCount + 1) * 1000);
        return;
      }
      
      // Fallback: try to get wallets from the existing walletGroups
      if (walletGroups.length > 0) {
        const allWallets = walletGroups.flatMap(group => 
          group.wallets.map(wallet => ({
            ...wallet,
            is_grouped: true,
            group_name: group.name,
            sol_balance: wallet.balance || 0,
            address: wallet.publicKey || wallet.address // Handle different address field names
          }))
        );
        console.log('Using fallback wallets from groups:', allWallets);
        setAvailableFundingWallets(allWallets);
      }
    }
  };

  const loadEligibleWallets = async () => {
    try {
      const response = await api.get('/sol-auto-distribution/eligible-wallets', {
        params: { token_id: tokenId }
      });
      if (response.data.success) {
        setEligibleWallets(response.data.data.eligible_wallets);
      }
    } catch (error) {
      console.error('Failed to load eligible wallets:', error);
    }
  };

  const loadAutoDistributionLogs = async () => {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/sol-auto-distribution/logs?limit=20&_t=${timestamp}`);
      if (response.data.success) {
        // Ensure logs are sorted by creation date (newest first)
        const sortedLogs = response.data.data.logs.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setAutoDistributionLogs(sortedLogs);
      }
    } catch (error) {
      console.error('Failed to load auto distribution logs:', error);
    }
  };

  const updateAutoDistributionSettings = async (updates) => {
    try {
      setIsLoading(true);
      const response = await api.put('/sol-auto-distribution/settings', updates);
      if (response.data.success) {
        setAutoDistributionSettings(response.data.data);
        setSuccessMessage('Auto distribution settings updated successfully');
        // Refresh related data
        loadEligibleWallets();
      }
    } catch (error) {
      console.error('Failed to update auto distribution settings:', error);
      setError(error.response?.data?.error || 'Failed to update settings');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerAutoDistribution = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/sol-auto-distribution/trigger');
      if (response.data.success) {
        setSuccessMessage('Auto distribution triggered successfully');
        // Refresh logs and eligible wallets after a short delay
        setTimeout(() => {
          loadAutoDistributionLogs();
          loadEligibleWallets();
          loadWalletsWithGroups(); // Refresh balances
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to trigger auto distribution:', error);
      setError(error.response?.data?.error || 'Failed to trigger auto distribution');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleSelectAllInGroup = (groupId) => {
    const group = walletGroups.find(g => g.id === groupId);
    if (!group) return;

    // Check if all wallets in this group are already selected
    const eligibleWallets = group.wallets.filter(w => {
      if (activeTab === 'distribute') {
        return w.id !== selectedSourceWallet?.id;
      } else {
        // For gather mode, exclude destination wallet and wallets with insufficient balance
        return w.id !== selectedDestWallet?.id && (w.balance || 0) > 0.00089;
      }
    });

    console.log(`[DEBUG] toggleSelectAllInGroup - Group: ${group.name}, Eligible wallets:`, eligibleWallets.length);
    console.log(`[DEBUG] Eligible wallet balances:`, eligibleWallets.map(w => ({ id: w.id, name: w.name, balance: w.balance })));
    console.log(`[DEBUG] Current gathers:`, gathers.map(g => ({ walletId: g.walletId, name: g.wallet?.name })));

    // If no eligible wallets, do nothing
    if (eligibleWallets.length === 0) {
      console.log(`[DEBUG] No eligible wallets in group ${group.name}, skipping`);
      return;
    }

    const isAllSelected = activeTab === 'distribute' 
      ? eligibleWallets.every(w => distributions.some(d => d.walletId === w.id))
      : eligibleWallets.every(w => gathers.some(g => g.walletId === w.id));

    console.log(`[DEBUG] isAllSelected:`, isAllSelected);

    if (isAllSelected) {
      // Unselect all wallets in this group
      if (activeTab === 'distribute') {
        setDistributions(prev => prev.filter(d => !group.wallets.some(w => w.id === d.walletId)));
        
        // Check if we need to mark for recalculation (only for equal/random modes)
        if ((distributeMode === 'equal' || distributeMode === 'random') && 
            distributions.some(d => d.amount > 0)) {
          setNeedsRecalculation(true);
        }
      } else {
        setGathers(prev => prev.filter(g => !group.wallets.some(w => w.id === g.walletId)));
      }
    } else {
      // Select all eligible wallets in this group
      if (activeTab === 'distribute') {
        const newDistributions = eligibleWallets
          .filter(w => !distributions.some(d => d.walletId === w.id))
          .map(wallet => ({
            walletId: wallet.id,
            wallet: wallet,
            amount: 0
          }));
        
        setDistributions(prev => [...prev, ...newDistributions]);
        
        // Check if we need to mark for recalculation (only for equal/random modes)
        if ((distributeMode === 'equal' || distributeMode === 'random') && 
            distributions.some(d => d.amount > 0)) {
          setNeedsRecalculation(true);
        }
      } else {
        const newGathers = eligibleWallets
          .filter(w => !gathers.some(g => g.walletId === w.id))
          .map(wallet => ({
            walletId: wallet.id,
            wallet: wallet,
            amount: Math.max(0, (wallet.balance || 0) - 0.00089) // Leave minimum for rent
          }));
        
        setGathers(prev => [...prev, ...newGathers]);
      }
    }
  };

  const getSelectAllButtonText = (groupId) => {
    const group = walletGroups.find(g => g.id === groupId);
    if (!group) return 'Select All';

    const eligibleWallets = group.wallets.filter(w => {
      if (activeTab === 'distribute') {
        return w.id !== selectedSourceWallet?.id;
      } else {
        // For gather mode, exclude destination wallet and wallets with insufficient balance
        return w.id !== selectedDestWallet?.id && (w.balance || 0) > 0.00089;
      }
    });

    // If no eligible wallets, return disabled state
    if (eligibleWallets.length === 0) {
      return 'No Eligible Wallets';
    }

    const isAllSelected = activeTab === 'distribute' 
      ? eligibleWallets.every(w => distributions.some(d => d.walletId === w.id))
      : eligibleWallets.every(w => gathers.some(g => g.walletId === w.id));

    const selectedCount = activeTab === 'distribute'
      ? eligibleWallets.filter(w => distributions.some(d => d.walletId === w.id)).length
      : eligibleWallets.filter(w => gathers.some(g => g.walletId === w.id)).length;

    if (isAllSelected) {
      return 'Unselect All';
    } else if (selectedCount > 0) {
      return `Select All (${selectedCount}/${eligibleWallets.length})`;
    } else {
      return 'Select All';
    }
  };

  const toggleWalletSelection = (wallet) => {
    if (activeTab === 'distribute') {
      if (selectedSourceWallet?.id === wallet.id) return;

      const exists = distributions.find(d => d.walletId === wallet.id);
      if (exists) {
        setDistributions(distributions.filter(d => d.walletId !== wallet.id));
      } else {
        setDistributions([...distributions, {
          walletId: wallet.id,
          wallet: wallet,
          amount: 0
        }]);
      }
      
      // Check if we need to mark for recalculation (only for equal/random modes)
      if ((distributeMode === 'equal' || distributeMode === 'random') && 
          distributions.some(d => d.amount > 0)) {
        setNeedsRecalculation(true);
      }
    } else {
      if (selectedDestWallet?.id === wallet.id) return;

      const exists = gathers.find(g => g.walletId === wallet.id);
      if (exists) {
        setGathers(gathers.filter(g => g.walletId !== wallet.id));
      } else {
        const maxAmount = wallet.balance > 0.00089 ? wallet.balance - 0.00089 : 0;
        setGathers([...gathers, {
          walletId: wallet.id,
          wallet: wallet,
          amount: maxAmount
        }]);
      }
    }
  };

  const updateDistributionAmount = (walletId, amount) => {
    // Store the actual input value, convert to number only when needed for calculations
    setDistributions(distributions.map(d => 
      d.walletId === walletId ? { ...d, amount: amount } : d
    ));
  };

  const updateGatherAmount = (walletId, amount) => {
    // Store the actual input value, convert to number only when needed for calculations
    setGathers(gathers.map(g => 
      g.walletId === walletId ? { ...g, amount: amount } : g
    ));
  };

  const calculateDistributions = () => {
    console.log('Calculate button clicked:', { totalAmount, distributionsLength: distributions.length, distributeMode });
    
    if (!totalAmount || distributions.length === 0) {
      if (!totalAmount) {
        setError('Please enter a total amount to distribute');
      } else if (distributions.length === 0) {
        setError('Please select destination wallets first');
      }
      return;
    }

    // Clear any previous errors
    setError(null);
    
    const total = parseFloat(totalAmount);
    if (isNaN(total) || total <= 0) {
      setError('Please enter a valid positive amount');
      return;
    }
    
    // Check if source wallet has sufficient balance
    if (selectedSourceWallet && selectedSourceWallet.balance) {
      const estimatedFees = 0.000005 * distributions.length; // Conservative fee estimate
      const minimumRemainingBalance = minimumRentExempt + 0.001; // Rent-exempt minimum + buffer
      const requiredAmount = total + estimatedFees + minimumRemainingBalance;
      if (selectedSourceWallet.balance < requiredAmount) {
        const availableToDistribute = Math.max(0, selectedSourceWallet.balance - minimumRemainingBalance - estimatedFees);
        setError(`Insufficient balance. Source wallet has ${selectedSourceWallet.balance.toFixed(6)} SOL, but ${requiredAmount.toFixed(6)} SOL is required (${total.toFixed(6)} to distribute + ${estimatedFees.toFixed(6)} fees + ${minimumRemainingBalance.toFixed(6)} minimum balance). Maximum distributable: ${availableToDistribute.toFixed(6)} SOL`);
        return;
      }
    }
    
    if (distributeMode === 'equal') {
      const amountPerWallet = total / distributions.length;
      console.log('Equal distribution:', amountPerWallet, 'SOL per wallet');
      setDistributions(distributions.map(d => ({
        ...d,
        amount: amountPerWallet
      })));
      
      // Clear recalculation flag and show success message
      setNeedsRecalculation(false);
      setTimeout(() => {
        setSuccessMessage(`Calculated equal distribution: ${amountPerWallet.toFixed(6)} SOL per wallet`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }, 100);
    } else if (distributeMode === 'random') {
      const baseAmount = total / distributions.length;
      const variance = randomVariance / 100;
      const minAmount = baseAmount * (1 - variance);
      const maxAmount = baseAmount * (1 + variance);
      
      console.log('Random distribution:', { baseAmount, variance, minAmount, maxAmount });
      
      // Generate random amounts for each wallet
      const randomAmounts = distributions.map(() => {
        return Math.random() * (maxAmount - minAmount) + minAmount;
      });
      
      // Normalize to ensure total equals target amount
      const currentTotal = randomAmounts.reduce((sum, amount) => sum + amount, 0);
      const multiplier = total / currentTotal;
      
      const finalAmounts = randomAmounts.map(amount => amount * multiplier);
      console.log('Generated random amounts:', finalAmounts);
      
      setDistributions(distributions.map((d, index) => ({
        ...d,
        amount: finalAmounts[index]
      })));
      
      // Clear recalculation flag and show success message
      setNeedsRecalculation(false);
      const minGenerated = Math.min(...finalAmounts);
      const maxGenerated = Math.max(...finalAmounts);
      setTimeout(() => {
        setSuccessMessage(`Generated random distribution: ${minGenerated.toFixed(6)} - ${maxGenerated.toFixed(6)} SOL range`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }, 100);
    }
  };

  const initializeSteps = (type, count) => {
    const steps = [
      {
        id: 'validation',
        title: 'Preparing Operation',
        description: 'Validating requirements and preparing wallets',
        status: 'pending'
      },
      {
        id: 'processing',
        title: 'Blockchain Transactions',
        description: type === 'distribute' 
          ? `Sending SOL to ${count} wallets via blockchain`
          : `Collecting SOL from ${count} wallets via blockchain`,
        status: 'pending'
      },
      {
        id: 'completion',
        title: 'Finalizing Results',
        description: 'Processing results and updating balances',
        status: 'pending'
      }
    ];
    setProcessSteps(steps);
    setCurrentStep('validation');
  };

  const updateStepStatus = (stepId, status, description = null) => {
    setProcessSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status, ...(description && { description }) }
        : step
    ));
  };

  const executeDistribution = async () => {
    if (!selectedSourceWallet || distributions.length === 0) {
      setError('Please select a source wallet and at least one destination');
      return;
    }

    // Use WebSocket for large operations to avoid timeout
    const useWebSocket = distributions.length > 30;
    
    if (useWebSocket) {
      return executeDistributionWebSocket();
    } else {
      return executeDistributionHTTP();
    }
  };

  const executeDistributionHTTP = async () => {

    // Check if recalculation is needed for equal/random modes
    if ((distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation) {
      setError('Wallet selection changed after calculation. Please click "Calculate" again before distributing.');
      return;
    }

    // Validate that all distributions have positive amounts
    const totalToDistribute = distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    
    if (totalToDistribute <= 0) {
      if (distributeMode === 'equal' || distributeMode === 'random') {
        setError('Please enter a total amount and click "Calculate" to set distribution amounts');
      } else {
        setError('Please enter positive amounts for all selected destinations');
      }
      return;
    }

    // Check for any individual distributions with 0 or negative amounts
    const invalidDistributions = distributions.filter(d => (parseFloat(d.amount) || 0) <= 0);
    if (invalidDistributions.length > 0) {
      setError(`${invalidDistributions.length} destinations have invalid amounts. All amounts must be positive.`);
      return;
    }
    if (selectedSourceWallet.balance) {
      const estimatedFees = 0.000005 * distributions.length;
      const minimumRemainingBalance = minimumRentExempt + 0.001; // Rent-exempt minimum + buffer
      const requiredAmount = totalToDistribute + estimatedFees + minimumRemainingBalance;
      if (selectedSourceWallet.balance < requiredAmount) {
        const availableToDistribute = Math.max(0, selectedSourceWallet.balance - minimumRemainingBalance - estimatedFees);
        setError(`Insufficient balance. Source wallet has ${selectedSourceWallet.balance.toFixed(6)} SOL, but ${requiredAmount.toFixed(6)} SOL is required (${totalToDistribute.toFixed(6)} to distribute + ${estimatedFees.toFixed(6)} fees + ${minimumRemainingBalance.toFixed(6)} minimum balance). Maximum distributable: ${availableToDistribute.toFixed(6)} SOL`);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setDistributionResult(null);
    
    // Initialize progress steps
    initializeSteps('distribute', distributions.length);
    
    // Scroll to top to show progress
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Step 1: Start validation
      const batchCount = Math.ceil(distributions.length / 5);
      updateStepStatus('validation', 'loading', `Preparing distribution to ${distributions.length} wallets...`);
      
      const payload = {
        sourceWalletId: selectedSourceWallet.id,
        destinations: distributions.map(d => ({
          walletId: d.walletId,
          amount: parseFloat(d.amount) || 0
        }))
      };

      // Connect to WebSocket instead of using REST API
      updateStepStatus('validation', 'loading', 'Connecting to distribution service...');
      
      try {
        await solanaDistributionWS.connect();
        updateStepStatus('validation', 'completed', 'Connected - starting distribution');
        setCurrentStep('processing');
        updateStepStatus('processing', 'loading', `Processing ${distributions.length} transfers in ${batchCount} batches...`);
        
        // Set up WebSocket message handlers
        let resolveDistribution;
        let rejectDistribution;
        const distributionPromise = new Promise((resolve, reject) => {
          resolveDistribution = resolve;
          rejectDistribution = reject;
        });
        
        // Handle WebSocket messages
        const messageHandler = (data) => {
          console.log('WebSocket message:', data);
          
          switch (data.type) {
            case 'distribute_progress':
              // Update progress message
              updateStepStatus('processing', 'loading', data.message);
              break;
              
            case 'distribute_complete':
              // Distribution completed
              resolveDistribution({ data });
              break;
              
            case 'error':
              // Error occurred
              rejectDistribution(new Error(data.message));
              break;
              
            case 'heartbeat':
              // Heartbeat to keep connection alive
              console.log('Heartbeat received');
              break;
          }
        };
        
        // Subscribe to WebSocket messages
        solanaDistributionWS.subscribe('distribute', messageHandler);
        
        // Send distribution request via WebSocket
        await solanaDistributionWS.distribute(payload.sourceWalletId, payload.destinations);
        
        // Wait for distribution to complete
        const response = await distributionPromise;
        
        // Unsubscribe from messages
        solanaDistributionWS.unsubscribe('distribute', messageHandler);
        
        // Handle WebSocket response
        await handleDistributionResponse(response);
        
      } catch (wsError) {
        console.error('WebSocket error:', wsError);
        // Fall back to REST API if WebSocket fails
        updateStepStatus('validation', 'error', 'WebSocket connection failed, using standard API...');
        
        const apiCall = api.post('/solana-distribution/distribute', payload, {
          timeout: 600000 // 10 minutes for large distributions
        });
        
        const response = await apiCall;
        
        // Handle API response
        await handleDistributionResponse(response);
      }
    } catch (error) {
      console.error('Distribution error:', error);
      
      // Handle the error case
      if (error.message.includes('Server temporarily unavailable') || error.code === 503) {
        updateStepStatus('completion', 'error', '⚠️ Server timeout - operations may have succeeded. Check blockchain explorer.');
        setError('Server timeout - operations may have succeeded. Check blockchain explorer.');
      } else {
        updateStepStatus('completion', 'error', 'Distribution failed');
        setError(error.message || 'Distribution failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to handle distribution response
  const handleDistributionResponse = async (response) => {
    try {
      // Step 3: Completion
      updateStepStatus('processing', 'completed', 'All transaction batches processed');
      setCurrentStep('completion');
      updateStepStatus('completion', 'loading', 'Updating wallet balances...');
      
      if (response.data && response.data.success) {
        const result = response.data.data;
        const hasAnySuccess = result.summary.successfulTransfers > 0;
        const hasAnyFailures = result.errors && result.errors.length > 0;
        
        if (hasAnySuccess && !hasAnyFailures) {
          // Complete success
          updateStepStatus('completion', 'completed', 'Distribution completed successfully');
        } else if (hasAnySuccess && hasAnyFailures) {
          // Partial success
          updateStepStatus('completion', 'completed', `Completed with ${result.summary.successfulTransfers}/${result.summary.successfulTransfers + result.summary.failedTransfers} successful transfers`);
        } else if (!hasAnySuccess && hasAnyFailures) {
          // Complete failure
          updateStepStatus('completion', 'error', 'All transfers failed - check balance and try fewer wallets');
        }
        
        setDistributionResult(result);
        
        // Only clear selections if there was at least some success
        if (hasAnySuccess) {
          setDistributions([]);
          setTotalAmount('');
        }
        
        // Reload wallets to update balances
        await loadWalletsWithGroups();
        
        // Clear steps after a delay
        setTimeout(() => {
          setProcessSteps([]);
          setCurrentStep(null);
        }, 3000);
      } else {
        updateStepStatus('completion', 'error', 'Distribution failed');
        setError((response.data && response.data.error) || 'Distribution failed');
      }
    } catch (error) {
      console.error('Response handling error:', error);
      updateStepStatus('completion', 'error', 'Error processing distribution response');
      setError('Error processing distribution response');
    }
  };

  // Helper function to handle gather response
  const handleGatherResponse = async (response) => {
    try {
      // Step 3: Completion
      updateStepStatus('processing', 'completed', 'All gather transactions processed');
      setCurrentStep('completion');
      updateStepStatus('completion', 'loading', 'Updating wallet balances...');
      
      if (response.data && response.data.success) {
        const result = response.data.data;
        const hasAnySuccess = result.summary.successfulTransfers > 0;
        const hasAnyFailures = result.errors && result.errors.length > 0;
        
        if (hasAnySuccess && !hasAnyFailures) {
          // Complete success
          updateStepStatus('completion', 'completed', 'Gather operation completed successfully');
        } else if (hasAnySuccess && hasAnyFailures) {
          // Partial success
          updateStepStatus('completion', 'completed', `Completed with ${result.summary.successfulTransfers}/${result.summary.successfulTransfers + result.summary.failedTransfers} successful transfers`);
        } else if (!hasAnySuccess && hasAnyFailures) {
          // Complete failure
          updateStepStatus('completion', 'error', 'All gather operations failed - check wallet balances');
        }
        
        setDistributionResult(result);
        
        // Only clear selections if there was at least some success
        if (hasAnySuccess) {
          setGathers([]);
        }
        
        // Reload wallets to update balances
        await loadWalletsWithGroups();
        
        // Clear steps after a delay
        setTimeout(() => {
          setProcessSteps([]);
          setCurrentStep(null);
        }, 3000);
      } else {
        updateStepStatus('completion', 'error', 'Gather operation failed');
        setError((response.data && response.data.error) || 'Gather operation failed');
      }
    } catch (error) {
      console.error('Gather response handling error:', error);
      updateStepStatus('completion', 'error', 'Error processing gather response');
      setError('Error processing gather response');
    }
  };

  const executeDistributionWebSocket = async () => {
    // Check if recalculation is needed for equal/random modes
    if ((distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation) {
      setError('Wallet selection changed after calculation. Please click "Calculate" again before distributing.');
      return;
    }

    // Validate that all distributions have positive amounts
    const totalToDistribute = distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    
    if (totalToDistribute <= 0) {
      if (distributeMode === 'equal' || distributeMode === 'random') {
        setError('Please enter a total amount and click "Calculate" to set distribution amounts');
      } else {
        setError('Please enter positive amounts for all selected destinations');
      }
      return;
    }

    // Check for any individual distributions with 0 or negative amounts
    const invalidDistributions = distributions.filter(d => (parseFloat(d.amount) || 0) <= 0);
    if (invalidDistributions.length > 0) {
      setError(`${invalidDistributions.length} destinations have invalid amounts. All amounts must be positive.`);
      return;
    }

    if (selectedSourceWallet.balance) {
      const estimatedFees = 0.000005 * distributions.length;
      const minimumRemainingBalance = minimumRentExempt + 0.001;
      const requiredAmount = totalToDistribute + estimatedFees + minimumRemainingBalance;
      if (selectedSourceWallet.balance < requiredAmount) {
        const availableToDistribute = Math.max(0, selectedSourceWallet.balance - minimumRemainingBalance - estimatedFees);
        setError(`Insufficient balance. Source wallet has ${selectedSourceWallet.balance.toFixed(6)} SOL, but ${requiredAmount.toFixed(6)} SOL is required (${totalToDistribute.toFixed(6)} to distribute + ${estimatedFees.toFixed(6)} fees + ${minimumRemainingBalance.toFixed(6)} minimum balance). Maximum distributable: ${availableToDistribute.toFixed(6)} SOL`);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setDistributionResult(null);
    
    // Initialize progress steps
    initializeSteps('distribute', distributions.length);
    
    // Scroll to top to show progress
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Setup WebSocket message handler
      const callbackId = `distribute_${Date.now()}`;
      
      solanaDistributionWS.onMessage(callbackId, (data) => {
        switch (data.type) {
          case 'distribute_status':
            updateStepStatus('validation', 'loading', data.message);
            break;
            
          case 'distribute_progress':
            if (!currentStep || currentStep === 'validation') {
              updateStepStatus('validation', 'completed', 'Requirements validated - starting distribution');
              setCurrentStep('processing');
              updateStepStatus('processing', 'loading', data.message);
            } else {
              updateStepStatus('processing', 'loading', data.message);
            }
            break;
            
          case 'distribute_complete':
            updateStepStatus('processing', 'completed', 'All transaction batches processed');
            setCurrentStep('completion');
            updateStepStatus('completion', 'loading', 'Updating wallet balances...');
            
            const result = data.data;
            const hasAnySuccess = result.summary.successfulTransfers > 0;
            const hasAnyFailures = result.errors && result.errors.length > 0;
            
            if (hasAnySuccess && !hasAnyFailures) {
              updateStepStatus('completion', 'completed', 'Distribution completed successfully');
            } else if (hasAnySuccess && hasAnyFailures) {
              updateStepStatus('completion', 'completed', `Completed with ${result.summary.successfulTransfers}/${result.summary.successfulTransfers + result.summary.failedTransfers} successful transfers`);
            } else if (!hasAnySuccess && hasAnyFailures) {
              updateStepStatus('completion', 'error', 'All transfers failed - check balance and try fewer wallets');
            }
            
            setDistributionResult(result);
            
            if (hasAnySuccess) {
              setDistributions([]);
              setTotalAmount('');
            }
            
            loadWalletsWithGroups();
            
            setTimeout(() => {
              setProcessSteps([]);
              setCurrentStep(null);
              setIsLoading(false);
              solanaDistributionWS.offMessage(callbackId);
            }, 3000);
            break;
            
          case 'distribute_error':
            updateStepStatus('processing', 'error', 'Transaction processing failed');
            setError(data.error || 'Failed during blockchain transaction processing');
            setIsLoading(false);
            solanaDistributionWS.offMessage(callbackId);
            break;
            
          case 'disconnected':
            updateStepStatus('processing', 'error', 'Connection lost');
            setError('WebSocket connection lost - operations may have succeeded. Check blockchain explorer.');
            setIsLoading(false);
            solanaDistributionWS.offMessage(callbackId);
            break;
            
          case 'reconnecting':
            updateStepStatus('processing', 'loading', `Reconnecting... (attempt ${data.attempt}/${data.maxRetries})`);
            break;
            
          case 'reconnection_failed':
            updateStepStatus('processing', 'error', 'Reconnection failed - check your connection');
            setError('Failed to reconnect to server. Operations may have succeeded. Check blockchain explorer.');
            setIsLoading(false);
            solanaDistributionWS.offMessage(callbackId);
            break;
            
          case 'heartbeat':
            // Server is alive and processing - just log it
            console.log('Received heartbeat from server:', data.timestamp);
            break;
        }
      });

      // Start distribution via WebSocket
      await solanaDistributionWS.distribute(
        selectedSourceWallet.id,
        distributions.map(d => ({
          walletId: d.walletId,
          amount: parseFloat(d.amount) || 0
        }))
      );

    } catch (error) {
      console.error('WebSocket distribution error:', error);
      updateStepStatus('validation', 'error', 'Connection timeout - please retry');
      setError('WebSocket connection failed - please try again or use fewer wallets.');
      setIsLoading(false);
    }
  };

  const executeGather = async () => {
    if (!selectedDestWallet || gathers.length === 0) {
      setError('Please select a destination wallet and at least one source');
      return;
    }

    // Always try WebSocket first with HTTP fallback to avoid timeouts
    return executeGatherWebSocket();
  };


  const executeGatherWebSocket = async () => {
    // Validate that gather has valid amounts (for custom mode)
    if (gatherMode === 'custom') {
      const totalToGather = gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
      if (totalToGather <= 0) {
        setError('Please enter positive amounts for all selected source wallets');
        return;
      }

      const invalidGathers = gathers.filter(g => (parseFloat(g.amount) || 0) <= 0);
      if (invalidGathers.length > 0) {
        setError(`${invalidGathers.length} source wallets have invalid amounts. All amounts must be positive.`);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setDistributionResult(null);
    
    // Initialize progress steps
    initializeSteps('gather', gathers.length);
    
    // Scroll to top to show progress
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Step 1: Start validation
      updateStepStatus('validation', 'loading', `Preparing gather from ${gathers.length} wallets...`);
      
      const payload = {
        destinationWalletId: selectedDestWallet.id,
        sources: gatherMode === 'all' 
          ? gathers.map(g => g.walletId)
          : gathers.map(g => ({ walletId: g.walletId, amount: parseFloat(g.amount) || 0 })),
        gatherAll: gatherMode === 'all'
      };

      // Connect to WebSocket instead of using REST API
      updateStepStatus('validation', 'loading', 'Connecting to gather service...');
      
      try {
        await solanaDistributionWS.connect();
        updateStepStatus('validation', 'completed', 'Connected - starting gather');
        setCurrentStep('processing');
        updateStepStatus('processing', 'loading', `Gathering from ${gathers.length} wallets in parallel...`);
        
        // Set up WebSocket message handlers
        let resolveGather;
        let rejectGather;
        const gatherPromise = new Promise((resolve, reject) => {
          resolveGather = resolve;
          rejectGather = reject;
        });
        
        // Handle WebSocket messages
        const messageHandler = (data) => {
          console.log('WebSocket gather message:', data);
          
          switch (data.type) {
            case 'gather_progress':
              // Update progress message
              updateStepStatus('processing', 'loading', data.message);
              break;
              
            case 'gather_complete':
              // Gather completed
              resolveGather({ data });
              break;
              
            case 'error':
              // Error occurred
              rejectGather(new Error(data.message));
              break;
              
            case 'heartbeat':
              // Heartbeat to keep connection alive
              console.log('Heartbeat received');
              break;
          }
        };
        
        // Subscribe to WebSocket messages
        solanaDistributionWS.subscribe('gather', messageHandler);
        
        // Send gather request via WebSocket
        await solanaDistributionWS.gather(payload.destinationWalletId, payload.sources, payload.gatherAll);
        
        // Wait for gather to complete
        const response = await gatherPromise;
        
        // Unsubscribe from messages
        solanaDistributionWS.unsubscribe('gather', messageHandler);
        
        // Handle WebSocket response
        await handleGatherResponse(response);
        
      } catch (wsError) {
        console.error('WebSocket gather error:', wsError);
        // Fall back to REST API if WebSocket fails
        updateStepStatus('validation', 'error', 'WebSocket connection failed, using standard API...');
        
        const apiCall = api.post('/solana-distribution/gather', payload, {
          timeout: 600000 // 10 minutes for large operations
        });
        
        const response = await apiCall;
        
        // Handle API response
        await handleGatherResponse(response);
      }
    } catch (error) {
      console.error('Gather error:', error);
      
      // Handle the error case
      if (error.message.includes('Server temporarily unavailable') || error.code === 503) {
        updateStepStatus('completion', 'error', '⚠️ Server timeout - operations may have succeeded. Check blockchain explorer.');
        setError('Server timeout - operations may have succeeded. Check blockchain explorer.');
      } else {
        updateStepStatus('completion', 'error', 'Gather operation failed');
        setError(error.message || 'Gather operation failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text, e) => {
    e.stopPropagation();
    if (!text) {
      console.warn('No address to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      console.log('Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const renderWalletCard = (wallet) => {
    // Debug logging for problematic wallets
    if (!wallet.publicKey) {
      console.warn('Wallet missing publicKey:', wallet);
    }
    
    const isSelected = activeTab === 'distribute' 
      ? distributions.some(d => d.walletId === wallet.id)
      : gathers.some(g => g.walletId === wallet.id);

    const isSourceOrDest = (activeTab === 'distribute' && selectedSourceWallet?.id === wallet.id) ||
                          (activeTab === 'gather' && selectedDestWallet?.id === wallet.id);

    return (
      <div
        key={wallet.id}
        className={`relative p-1.5 sm:p-2 border rounded cursor-pointer transition-all hover:shadow-sm ${
          isSourceOrDest
            ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
            : isSelected
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
        }`}
        onClick={() => {
          if (activeTab === 'distribute' && !selectedSourceWallet) {
            setSelectedSourceWallet(wallet);
          } else if (activeTab === 'gather' && !selectedDestWallet) {
            setSelectedDestWallet(wallet);
          } else if (!isSourceOrDest) {
            toggleWalletSelection(wallet);
          }
        }}
      >
        <div className="space-y-0.5 sm:space-y-1">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors truncate text-xs sm:text-sm">{wallet.name}</h4>
          <div className="flex items-center justify-between">
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 transition-colors font-mono truncate flex-1">
              {wallet.publicKey ? `${wallet.publicKey.slice(0, 4)}...${wallet.publicKey.slice(-4)}` : 'No address'}
            </p>
            <button
              onClick={(e) => copyToClipboard(wallet.publicKey || '', e)}
              className="ml-1 p-0.5 sm:p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Copy address"
            >
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
          <p className="text-xs sm:text-sm font-semibold text-green-600 dark:text-green-400 transition-colors">
            {wallet.balance ? wallet.balance.toFixed(4) : '0.0000'} SOL
          </p>
        </div>
        
        {isSourceOrDest && (
          <div className="absolute top-1 right-1 px-1 py-0.5 bg-green-600 text-white text-xs font-bold rounded">
            {activeTab === 'distribute' ? 'SRC' : 'DEST'}
          </div>
        )}
        
        {isSelected && activeTab === 'distribute' && distributeMode === 'custom' && (
          <div className="mt-2">
            <input
              type="number"
              min="0"
              step="0.000001"
              placeholder="Amount"
              value={distributions.find(d => d.walletId === wallet.id)?.amount || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, decimals starting with ., and valid positive numbers
                if (value === '' || value === '.' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                  updateDistributionAmount(wallet.id, value);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
            />
          </div>
        )}
        
        {isSelected && activeTab === 'distribute' && (distributeMode === 'equal' || distributeMode === 'random') && distributions.find(d => d.walletId === wallet.id)?.amount > 0 && (
          <div className="mt-2 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-600 rounded px-2 py-1 transition-colors">
            <div className="text-xs text-purple-700 dark:text-purple-300 font-medium transition-colors">
              Will receive: {parseFloat(distributions.find(d => d.walletId === wallet.id)?.amount || 0).toFixed(6)} SOL
            </div>
          </div>
        )}
        
        {isSelected && activeTab === 'gather' && gatherMode === 'custom' && (
          <div className="mt-2">
            <input
              type="number"
              min="0"
              step="0.000001"
              placeholder="Amount"
              value={gathers.find(g => g.walletId === wallet.id)?.amount || ''}
              onChange={(e) => {
                const value = e.target.value;
                const numericValue = parseFloat(value) || 0;
                const maxValue = wallet.balance - 0.00089;
                // Allow empty string, decimals starting with ., and valid numbers within range
                if (value === '' || value === '.' || (!isNaN(numericValue) && numericValue >= 0 && numericValue <= maxValue)) {
                  updateGatherAmount(wallet.id, value);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              max={wallet.balance - 0.00089}
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full p-2 sm:p-4 bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto">
        <div className="mb-3 sm:mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1 transition-colors">Solana Distribution Center</h1>
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm transition-colors">Efficiently manage SOL distribution across your wallets</p>
        </div>

        {/* Tab Navigation - Mobile Optimized */}
        <div className="flex justify-center mb-4">
          <div className="flex w-full sm:w-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-1 transition-colors">
                <button
                  className={`flex-1 sm:flex-initial px-2 sm:px-6 py-2 sm:py-3 rounded-md font-medium transition-colors flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-2 ${
                    activeTab === 'distribute'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-600'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  }`}
                  onClick={() => {
                    setActiveTab('distribute');
                    setSelectedDestWallet(null);
                    setGathers([]);
                    setDistributionResult(null);
                    setError(null);
                    setSuccessMessage(null);
                    setProcessSteps([]);
                    setCurrentStep(null);
                  }}
                >
                  <span className="text-base sm:text-base">📤</span>
                  <span className="text-xs sm:text-base">Distribute</span>
                </button>
                <button
                  className={`flex-1 sm:flex-initial px-2 sm:px-6 py-2 sm:py-3 rounded-md font-medium transition-colors flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-2 mx-1 ${
                    activeTab === 'gather'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-600'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  }`}
                  onClick={() => {
                    setActiveTab('gather');
                    setSelectedSourceWallet(null);
                    setDistributions([]);
                    setDistributionResult(null);
                    setError(null);
                    setSuccessMessage(null);
                    setProcessSteps([]);
                    setCurrentStep(null);
                  }}
                >
                  <span className="text-base sm:text-base">📥</span>
                  <span className="text-xs sm:text-base">Gather</span>
                </button>
                <button
                  className={`flex-1 sm:flex-initial px-2 sm:px-6 py-2 sm:py-3 rounded-md font-medium transition-colors flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-2 ${
                    activeTab === 'auto'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-600'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  }`}
                  onClick={() => {
                    setActiveTab('auto');
                    setSelectedSourceWallet(null);
                    setSelectedDestWallet(null);
                    setDistributions([]);
                    setGathers([]);
                    setDistributionResult(null);
                    setError(null);
                    setSuccessMessage(null);
                    setProcessSteps([]);
                    setCurrentStep(null);
                  }}
                >
                  <span className="text-base sm:text-base">🤖</span>
                  <span className="text-xs sm:text-base">Auto</span>
                </button>
              </div>
            </div>

            {/* Progress Steps */}
            {processSteps.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 mb-6 transition-colors">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {activeTab === 'distribute' ? 'Distribution' : 'Gather'} Progress
                </h3>
                <div className="space-y-4">
                  {processSteps.map((step) => (
                    <div key={step.id} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 mt-1">
                        {step.status === 'completed' && (
                          <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center transition-colors">
                            <svg className="w-4 h-4 text-green-600 dark:text-green-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        {step.status === 'loading' && (
                          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center transition-colors">
                            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin transition-colors" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        )}
                        {step.status === 'error' && (
                          <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center transition-colors">
                            <svg className="w-4 h-4 text-red-600 dark:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        )}
                        {step.status === 'pending' && (
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h4 className={`text-sm font-medium ${
                            step.status === 'completed' ? 'text-green-900 dark:text-green-300' :
                            step.status === 'loading' ? 'text-blue-900 dark:text-blue-300' :
                            step.status === 'error' ? 'text-red-900 dark:text-red-300' :
                            'text-gray-500 dark:text-gray-400 transition-colors'
                          }`}>
                            {step.title}
                          </h4>
                          {step.status === 'loading' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 transition-colors">
                              In Progress
                            </span>
                          )}
                          {step.status === 'completed' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 transition-colors">
                              Complete
                            </span>
                          )}
                          {step.status === 'error' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 transition-colors">
                              Failed
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${
                          step.status === 'completed' ? 'text-green-700 dark:text-green-400' :
                          step.status === 'loading' ? 'text-blue-700 dark:text-blue-400' :
                          step.status === 'error' ? 'text-red-700 dark:text-red-400' :
                          'text-gray-500 dark:text-gray-400 transition-colors'
                        }`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Progress bar */}
                <div className="mt-6">
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 transition-colors mb-2">
                    <span>Progress</span>
                    <span>{Math.round(((processSteps.filter(s => s.status === 'completed').length) / processSteps.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300 ease-in-out"
                      style={{ 
                        width: `${((processSteps.filter(s => s.status === 'completed').length) / processSteps.length) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-4 mb-6 transition-colors">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✅</span>
                  <span className="text-green-700 dark:text-green-300 transition-colors">{successMessage}</span>
                </div>
              </div>
            )}

            {/* Recalculation Warning */}
            {activeTab === 'distribute' && (distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation && (
              <div className="bg-orange-50 dark:bg-orange-900/50 border border-orange-200 dark:border-orange-800 rounded-md p-4 mb-6 transition-colors">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-orange-700 dark:text-orange-300 font-medium transition-colors">Wallet selection changed after calculation. Please click "Calculate" to update amounts before distributing.</span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6 transition-colors">
                <div className="flex items-center">
                  <span className="text-red-500 dark:text-red-400 mr-2 transition-colors">⚠️</span>
                  <span className="text-red-700 dark:text-red-300 transition-colors">{error}</span>
                </div>
              </div>
            )}

            {/* Operation Result */}
            {distributionResult && (() => {
              const totalTransfers = distributionResult.summary.successfulTransfers + distributionResult.summary.failedTransfers;
              const successRate = totalTransfers > 0 ? (distributionResult.summary.successfulTransfers / totalTransfers) * 100 : 0;
              const isSuccess = distributionResult.summary.successfulTransfers > 0;
              const isPartialSuccess = isSuccess && distributionResult.errors && distributionResult.errors.length > 0;
              const isCompleteFailure = distributionResult.summary.successfulTransfers === 0 && distributionResult.errors && distributionResult.errors.length > 0;
              
              return (
                <div className={`border rounded-md p-6 mb-6 ${
                  isCompleteFailure ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800' : 
                  isPartialSuccess ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800' : 
                  'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
                }`}>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                    isCompleteFailure ? 'text-red-800 dark:text-red-300' : 
                    isPartialSuccess ? 'text-yellow-800 dark:text-yellow-300' : 
                    'text-green-800 dark:text-green-300'
                  }`}>
                    <span className="mr-2">
                      {isCompleteFailure ? '❌' : isPartialSuccess ? '⚠️' : '✅'}
                    </span>
                    {isCompleteFailure ? 'Operation Failed' : 
                     isPartialSuccess ? 'Partially Completed' : 
                     'Operation Completed'}
                  </h3>
                  
                  <div className={`space-y-2 ${
                    isCompleteFailure ? 'text-red-700 dark:text-red-400' : 
                    isPartialSuccess ? 'text-yellow-700 dark:text-yellow-400' : 
                    'text-green-700 dark:text-green-400'
                  }`}>
                    {activeTab === 'distribute' ? (
                      <>
                        <p>Distributed {distributionResult.summary.totalDistributed} SOL to {distributionResult.summary.successfulTransfers} wallets</p>
                        <p>Source Balance: {distributionResult.sourceWallet.initialBalance.toFixed(4)} → {distributionResult.sourceWallet.finalBalance.toFixed(4)} SOL</p>
                      </>
                    ) : (
                      <>
                        <p>Gathered {distributionResult.summary.totalGathered.toFixed(4)} SOL from {distributionResult.summary.successfulTransfers} wallets</p>
                        <p>Destination Balance: {distributionResult.destinationWallet.finalBalance.toFixed(4)} SOL</p>
                      </>
                    )}
                    
                    {totalTransfers > 0 && (
                      <p>Success Rate: {distributionResult.summary.successfulTransfers}/{totalTransfers} ({successRate.toFixed(0)}%)</p>
                    )}
                  </div>
                  
                  {distributionResult.errors && distributionResult.errors.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md transition-colors">
                      <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2 transition-colors">
                        {isCompleteFailure ? 'Why it failed:' : 'Issues encountered:'}
                      </h4>
                      <div className="space-y-1">
                        {distributionResult.errors.slice(0, 3).map((err, idx) => (
                          <p key={idx} className="text-red-700 dark:text-red-400 text-sm transition-colors">
                            {err.walletName}: {translateError(err.error)}
                          </p>
                        ))}
                        {distributionResult.errors.length > 3 && (
                          <p className="text-red-600 dark:text-red-400 text-sm font-medium transition-colors">
                            + {distributionResult.errors.length - 3} more wallets with similar issues
                          </p>
                        )}
                      </div>
                      
                      {isCompleteFailure && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded text-sm transition-colors">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-blue-800">
                              <p className="font-medium">Suggestions:</p>
                              <ul className="text-xs mt-1 space-y-1">
                                <li>• Add more SOL to your source wallet</li>
                                <li>• Reduce the amount per wallet</li>
                                <li>• Select fewer destination wallets</li>
                                <li>• Check that wallets have valid addresses</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Controls Section - Responsive Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              {activeTab === 'distribute' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Distribution Settings</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="equal"
                            checked={distributeMode === 'equal'}
                            onChange={(e) => {
                              setDistributeMode(e.target.value);
                              setNeedsRecalculation(false); // Clear flag when mode changes
                            }}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>Equal Distribution</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="custom"
                            checked={distributeMode === 'custom'}
                            onChange={(e) => setDistributeMode(e.target.value)}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>Custom Amounts</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="random"
                            checked={distributeMode === 'random'}
                            onChange={(e) => setDistributeMode(e.target.value)}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>Random Range</span>
                        </label>
                      </div>
                      
                      {distributeMode === 'custom' && (
                        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300 transition-colors">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="font-medium mb-1">How to use:</p>
                              <ol className="text-xs space-y-1 list-decimal list-inside text-blue-700 dark:text-blue-400 transition-colors">
                                <li>Select a source wallet (contains SOL to distribute)</li>
                                <li>Select destination wallets (click to turn them purple)</li>
                                <li>Enter custom amounts in each wallet's input field</li>
                                <li>Click "Execute Distribution" to perform the transfers</li>
                              </ol>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {(distributeMode === 'equal' || distributeMode === 'random') && (
                        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300 transition-colors">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="font-medium mb-1">How to use:</p>
                              <ol className="text-xs space-y-1 list-decimal list-inside text-blue-700 dark:text-blue-400 transition-colors">
                                <li>Select a source wallet (contains SOL to distribute)</li>
                                <li>Select destination wallets (click to turn them purple)</li>
                                <li>Enter total amount below</li>
                                <li>Click "{distributeMode === 'equal' ? 'Calculate' : 'Generate Random Distribution'}" to set amounts</li>
                                <li>Click "Execute Distribution" to perform the transfers</li>
                              </ol>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {(distributeMode === 'equal' || distributeMode === 'random') && (
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                            Total Amount to Distribute:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.000001"
                            value={totalAmount}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string, decimals starting with ., and valid numbers
                              if (value === '' || value === '.' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                setTotalAmount(value);
                                setNeedsRecalculation(false); // Clear flag when total amount changes
                              }
                            }}
                            placeholder="Enter total SOL"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                          />
                          {distributeMode === 'random' && (
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                                Variance: {randomVariance}%
                              </label>
                              <div className="relative">
                                <input
                                  type="range"
                                  min="1"
                                  max="50"
                                  value={randomVariance}
                                  onChange={(e) => setRandomVariance(parseInt(e.target.value))}
                                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-purple"
                                  style={{
                                    background: `linear-gradient(to right, #9333ea 0%, #9333ea ${(randomVariance - 1) / 49 * 100}%, #e5e7eb ${(randomVariance - 1) / 49 * 100}%, #e5e7eb 100%)`
                                  }}
                                />
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 transition-colors mt-1">
                                  <span>1%</span>
                                  <span>25%</span>
                                  <span>50%</span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                Controls the difference between lowest and highest wallet amounts
                              </p>
                            </div>
                          )}
                          <button 
                            onClick={calculateDistributions}
                            className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
                          >
                            {distributeMode === 'random' ? 'Generate Random Distribution' : 'Calculate'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Source Wallet</h3>
                    {selectedSourceWallet ? (
                      <div className="space-y-3">
                        <div className="text-center">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{selectedSourceWallet.name}</h4>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400 my-2 transition-colors">{selectedSourceWallet.balance ? selectedSourceWallet.balance.toFixed(4) : '0.0000'} SOL</p>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">Address:</label>
                          <div className="flex items-center space-x-2">
                            <p className="text-xs font-mono text-gray-600 dark:text-gray-400 flex-1 break-all leading-relaxed transition-colors">
                              {selectedSourceWallet.publicKey || 'No address available'}
                            </p>
                            <button
                              onClick={(e) => copyToClipboard(selectedSourceWallet.publicKey || '', e)}
                              className="flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                              title="Copy full address"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="text-center">
                          <button 
                            onClick={() => setSelectedSourceWallet(null)}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Change Source
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center transition-colors">
                          <svg className="w-8 h-8 text-blue-500 dark:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 transition-colors">Choose Your Source Wallet</h4>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-sm mx-auto transition-colors">
                          Select the wallet below that contains the SOL you want to distribute to other wallets
                        </p>
                        <div className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-lg transition-colors">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Click any wallet below to select it as your source
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'gather' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Gather Settings</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="all"
                            checked={gatherMode === 'all'}
                            onChange={(e) => setGatherMode(e.target.value)}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>Gather All Available</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="custom"
                            checked={gatherMode === 'custom'}
                            onChange={(e) => setGatherMode(e.target.value)}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span>Custom Amounts</span>
                        </label>
                      </div>
                      
                      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300 transition-colors">
                        <div className="flex items-start space-x-2">
                          <svg className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="font-medium mb-1">How to use:</p>
                            <ol className="text-xs space-y-1 list-decimal list-inside">
                              <li>Select a destination wallet (where SOL will be collected)</li>
                              <li>Select source wallets (click to turn them purple)</li>
                              <li>Choose "Gather All" or "Custom Amounts"</li>
                              <li>Click "Execute Gather" to collect SOL</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Destination Wallet</h3>
                    {selectedDestWallet ? (
                      <div className="space-y-3">
                        <div className="text-center">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{selectedDestWallet.name}</h4>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400 my-2 transition-colors">{selectedDestWallet.balance ? selectedDestWallet.balance.toFixed(4) : '0.0000'} SOL</p>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">Address:</label>
                          <div className="flex items-center space-x-2">
                            <p className="text-xs font-mono text-gray-600 dark:text-gray-400 flex-1 break-all leading-relaxed transition-colors">
                              {selectedDestWallet.publicKey || 'No address available'}
                            </p>
                            <button
                              onClick={(e) => copyToClipboard(selectedDestWallet.publicKey || '', e)}
                              className="flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                              title="Copy full address"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="text-center">
                          <button 
                            onClick={() => setSelectedDestWallet(null)}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Change Destination
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-purple-50 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 transition-colors">Choose Your Destination Wallet</h4>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-sm mx-auto transition-colors">
                          Select the wallet below where you want to collect all the gathered SOL
                        </p>
                        <div className="inline-flex items-center text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-2 rounded-lg">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Click any wallet below to select it as your destination
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'auto' && (
                <>
                  {/* Show loading message if wallet data isn't ready */}
                  {walletGroups.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600 dark:text-gray-400 transition-colors">Loading wallet data...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Auto Distribution Settings */}
                  {walletGroups.length > 0 && (
                    <>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4 flex items-center">
                      <span className="mr-2">🤖</span>
                      Auto Distribution Settings
                      {autoDistributionLoading && (
                        <div className="ml-3 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 transition-colors">Loading...</span>
                        </div>
                      )}
                    </h3>
                    
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between mb-6 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg transition-colors">
                      <div className="flex items-center space-x-2">
                        <span className="text-purple-600 dark:text-purple-400">🤖</span>
                        <div>
                          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Enable Auto Distribution</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Automatically top up wallets in groups when they fall below minimum balance</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateAutoDistributionSettings({ enabled: !autoDistributionSettings.enabled })}
                        disabled={isLoading}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                          autoDistributionSettings.enabled ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-200 transition-transform ${
                            autoDistributionSettings.enabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* Funding Wallet Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors mb-2">
                          Funding Wallet (Source of SOL)
                        </label>
                        <select
                          value={autoDistributionSettings.funding_wallet_id || ''}
                          onChange={(e) => {
                            const walletId = e.target.value ? parseInt(e.target.value) : null;
                            const wallet = availableFundingWallets.find(w => w.id === walletId);
                            console.log('Selected wallet:', wallet);
                            updateAutoDistributionSettings({ 
                              funding_wallet_id: walletId,
                              funding_wallet: wallet 
                            });
                          }}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                          disabled={isLoading}
                        >
                          <option value="">
                            {walletGroups.length === 0 
                              ? 'Loading wallets...' 
                              : availableFundingWallets.length === 0 
                                ? 'No wallets available - check console for errors' 
                                : 'Select a funding wallet...'}
                          </option>
                          {availableFundingWallets.map(wallet => (
                            <option key={wallet.id} value={wallet.id}>
                              {wallet.name} - {formatAddress(wallet.address)} - {(wallet.sol_balance || 0).toFixed(4)} SOL
                              {wallet.is_grouped && ` (${wallet.group_name})`}
                            </option>
                          ))}
                        </select>
                        {availableFundingWallets.length > 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors mt-1">
                            Recommendation: {availableFundingWallets.filter(w => !w.is_grouped).length > 0 ? 'Use ungrouped wallets to avoid circular distribution' : 'Consider adding ungrouped wallets as funding sources'}
                          </p>
                        )}
                        
                        {/* Selected wallet full address display */}
                        {autoDistributionSettings.funding_wallet && (
                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md transition-colors">
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-300 transition-colors">Selected Funding Wallet:</p>
                            <p className="text-sm text-blue-800 dark:text-blue-400 transition-colors">{autoDistributionSettings.funding_wallet.name}</p>
                            <p className="text-xs font-mono text-blue-700 dark:text-blue-400 break-all transition-colors">
                              {formatAddress(autoDistributionSettings.funding_wallet.address, true)}
                            </p>

                          </div>
                        )}
                        
                      </div>

                      {/* Amount Settings */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors mb-2">
                            Minimum Balance (SOL)
                          </label>
                          <input
                            type="number"
                            min="0.009"
                            step="0.001"
                            value={autoDistributionSettings.minimum_balance}
                            onChange={(e) => {
                              // Allow any input during editing
                              const value = e.target.value;
                              if (value === '' || value === '.') {
                                // Allow empty or just decimal point
                                setAutoDistributionSettings(prev => ({ ...prev, minimum_balance: value }));
                              } else {
                                const numericValue = parseFloat(value);
                                if (!isNaN(numericValue)) {
                                  setAutoDistributionSettings(prev => ({ ...prev, minimum_balance: numericValue }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // Enforce minimum when user leaves the field
                              const value = parseFloat(e.target.value);
                              if (isNaN(value) || value < 0.009) {
                                updateAutoDistributionSettings({ minimum_balance: 0.009 });
                              } else {
                                updateAutoDistributionSettings({ minimum_balance: value });
                              }
                            }}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                            placeholder="0.009"
                            disabled={isLoading}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors mt-1">Trigger threshold for auto top-up (minimum 0.009 SOL)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors mb-2">
                            Top-up Amount (SOL)
                          </label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.001"
                            value={autoDistributionSettings.top_up_amount}
                            onChange={(e) => {
                              // Allow any input during editing
                              const value = e.target.value;
                              if (value === '' || value === '.') {
                                // Allow empty or just decimal point
                                setAutoDistributionSettings(prev => ({ ...prev, top_up_amount: value }));
                              } else {
                                const numericValue = parseFloat(value);
                                if (!isNaN(numericValue)) {
                                  setAutoDistributionSettings(prev => ({ ...prev, top_up_amount: numericValue }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // Enforce minimum when user leaves the field
                              const value = parseFloat(e.target.value);
                              if (isNaN(value) || value < 0.01) {
                                updateAutoDistributionSettings({ top_up_amount: 0.01 });
                              } else {
                                updateAutoDistributionSettings({ top_up_amount: value });
                              }
                            }}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                            placeholder="0.01"
                            disabled={isLoading}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors mt-1">Amount to send per top-up (minimum 0.01 SOL)</p>
                        </div>
                      </div>
                    </div>

                    {/* Manual Trigger */}
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 transition-colors">
                      <button
                        onClick={triggerAutoDistribution}
                        disabled={isLoading || !autoDistributionSettings.enabled || !autoDistributionSettings.funding_wallet_id}
                        className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                          isLoading || !autoDistributionSettings.enabled || !autoDistributionSettings.funding_wallet_id
                            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed transition-colors'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {isLoading ? 'Processing...' : 'Trigger Auto Distribution Now'}
                      </button>
                    </div>
                  </div>

                  {/* Status and Eligible Wallets */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Status & Eligible Wallets</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 transition-colors">
                        <div className="text-sm text-blue-600 dark:text-blue-400 font-medium transition-colors">Eligible for Top-up</div>
                        <div className="text-2xl font-bold text-blue-700 dark:text-blue-300 transition-colors">{eligibleWallets.length}</div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 transition-colors">
                        <div className="text-sm text-green-600 dark:text-green-400 font-medium transition-colors">Minimum Balance</div>
                        <div className="text-2xl font-bold text-green-700 dark:text-green-300 transition-colors">{autoDistributionSettings.minimum_balance} SOL</div>
                      </div>
                    </div>

                    {eligibleWallets.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors">Wallets needing top-up:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                          {eligibleWallets.slice(0, 12).map(wallet => (
                            <div key={wallet.id} className="flex justify-between items-center p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded transition-colors">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">{wallet.name}</span>
                              <span className="text-sm text-orange-600 dark:text-orange-400 transition-colors">{wallet.sol_balance?.toFixed(4) || '0.0000'} SOL</span>
                            </div>
                          ))}
                        </div>
                        {eligibleWallets.length > 12 && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">+ {eligibleWallets.length - 12} more wallets need top-up</p>
                        )}
                      </div>
                    )}

                    {eligibleWallets.length === 0 && autoDistributionSettings.enabled && (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400 transition-colors">
                        <span className="text-green-500 dark:text-green-400 mr-2 transition-colors">✅</span>
                        All active grouped wallets have sufficient balance
                      </div>
                    )}
                  </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Auto Distribution Logs */}
            {activeTab === 'auto' && autoDistributionLogs.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">Recent Distribution Logs</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {autoDistributionLogs.map(log => (
                    <div key={log.id} className={`flex justify-between items-center p-3 rounded-lg ${
                      log.status === 'completed' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800' :
                      log.status === 'failed' ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800' :
                      'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                    }`}>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${
                            log.status === 'completed' ? 'text-green-800 dark:text-green-300' :
                            log.status === 'failed' ? 'text-red-800 dark:text-red-300' :
                            'text-yellow-800 dark:text-yellow-300'
                          }`}>
                            {log.from_wallet_name} → {log.to_wallet_name}
                          </span>
                          {log.status === 'completed' && <span className="text-green-500 dark:text-green-400 transition-colors">✅</span>}
                          {log.status === 'failed' && <span className="text-red-500 dark:text-red-400 transition-colors">❌</span>}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors mt-1 space-y-1">
                          <div className="flex items-center space-x-2">
                            <span>From:</span>
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400 transition-colors">
                              {log.from_wallet_address?.slice(0, 4)}...{log.from_wallet_address?.slice(-4)}
                            </span>
                            <button
                              onClick={(e) => copyToClipboard(log.from_wallet_address, e)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="Copy address"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>To:</span>
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400 transition-colors">
                              {log.to_wallet_address?.slice(0, 4)}...{log.to_wallet_address?.slice(-4)}
                            </span>
                            <button
                              onClick={(e) => copyToClipboard(log.to_wallet_address, e)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="Copy address"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors mt-1">
                          {new Date(log.created_at).toLocaleString()}
                          {log.error_message && ` - ${log.error_message}`}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">
                        {log.amount_sent} SOL
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wallets Section */}
            {activeTab !== 'auto' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors mb-4">
                  {activeTab === 'distribute' 
                    ? (selectedSourceWallet ? 'Select Destination Wallets' : 'Select Source Wallet')
                    : (selectedDestWallet ? 'Select Source Wallets' : 'Select Destination Wallet')
                  }
              </h3>
              
              <div className="space-y-3">
                {walletGroups.map(group => (
                  <div key={group.id} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden transition-colors">
                    <div 
                      className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: group.color }}
                        ></div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{group.name}</h4>
                        <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">({group.wallets.length} wallets)</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const buttonText = getSelectAllButtonText(group.id);
                            if (buttonText !== 'No Eligible Wallets') {
                              toggleSelectAllInGroup(group.id);
                            }
                          }}
                          className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                            getSelectAllButtonText(group.id) === 'No Eligible Wallets'
                              ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/40 transition-colors'
                          }`}
                          disabled={getSelectAllButtonText(group.id) === 'No Eligible Wallets'}
                        >
                          {getSelectAllButtonText(group.id)}
                        </button>
                        <span className="text-gray-400 dark:text-gray-500 transition-colors">
                          {expandedGroups[group.id] ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    {expandedGroups[group.id] && (
                      <div className="p-3">
                        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-2 ${
                          group.wallets.length > 32 ? 'max-h-64 overflow-y-auto' : ''
                        }`}>
                          {group.wallets.map(wallet => renderWalletCard(wallet))}
                        </div>
                        {group.wallets.length > 32 && (
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center transition-colors">
                            {group.wallets.length} wallets (scroll to see more)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              </div>
            )}

            {/* Action Section */}
            {activeTab !== 'auto' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors p-6">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                  {activeTab === 'distribute' ? (
                    <>
                      <p>Selected {distributions.length} destination wallets</p>
                      <p>Total to distribute: {distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0).toFixed(4)} SOL</p>
                    </>
                  ) : (
                    <>
                      <p>Selected {gathers.length} source wallets</p>
                      <p>Total to gather: {gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0).toFixed(4)} SOL</p>
                    </>
                  )}
                </div>
                <button
                  className={`px-8 py-3 rounded-md font-medium transition-colors ${
                    isLoading || (activeTab === 'distribute' 
                      ? !selectedSourceWallet || distributions.length === 0 || distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) <= 0 || ((distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation)
                      : !selectedDestWallet || gathers.length === 0 || (gatherMode === 'custom' && gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0) <= 0))
                      ? 'bg-gray-300 text-gray-500 dark:text-gray-400 transition-colors cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                  onClick={activeTab === 'distribute' ? executeDistribution : executeGather}
                  disabled={isLoading || (activeTab === 'distribute' 
                    ? !selectedSourceWallet || distributions.length === 0 || distributions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) <= 0 || ((distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation)
                    : !selectedDestWallet || gathers.length === 0 || (gatherMode === 'custom' && gathers.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0) <= 0))}
                >
                  {isLoading ? 'Processing...' : 
                   (activeTab === 'distribute' && (distributeMode === 'equal' || distributeMode === 'random') && needsRecalculation) ? 'Calculate First' :
                   activeTab === 'distribute' ? 'Execute Distribution' : 'Execute Gather'}
            </button>
          </div>
        </div>
            )}
      </div>
    </div>
  );
};

export default SolanaDistributionPage;